import React, { useRef } from "react";

import { EditorState } from "draft-js";
import { connect } from "react-redux";

import * as SC from "./SpellcheckLite.styled";
import { AdvicePanel } from "../../Components/Advice/AdvicePanel";
import { CorrectionModeButtons } from "../../Components/CorrectionModeButtons/CorrectionModeButtons";
import { ExpressPreview } from "../../Components/ExpressPreview/ExpressPreview";
import { Handshake } from "../../Components/Handshake/Handshake";
import { OneClickHandshake } from "../../Components/Handshake/OneClickHandshake";
import { InlineAdviceWrapper } from "../../Components/InlineAdvice/InlineAdviceWrapper";
import { MetaMenu } from "../../Components/MetaMenu/MetaMenu";
import { Pad } from "../../Components/Pad/Pad";
import { Profiler } from "../../Components/Profiler/Profiler";
import { Rim } from "../../Components/Rim/Rim";
import { StatusMessages } from "../../Components/StatusMessages/StatusMessages";
import useOneClickStore, { OneClickStore } from "../../Hooks/useOneClickStore";
import { FeatureFlagProps, selectFeatureFlags } from "../../Store/SettingsState";
import { AdviceErrorWrapper } from "../../Util/AdviceErrorWrapper";
import { GTMDataLayerPush } from "../../Util/GoogleTagManager";
import { PadErrorWrapper } from "../../Util/PadErrorWrapper";

type SpellcheckLiteStateProps = {
    featureFlags: FeatureFlagProps[];
}

const Spellcheck: React.FC<SpellcheckLiteStateProps> = ({ featureFlags }) => {
    const deskRef = useRef(null);
    const isExpressFeatureActive = featureFlags["click_correction"];
    const isExpressModeActive = useOneClickStore((store: OneClickStore) => store.isOneClickModeActive);
    const setExpressModeActive = useOneClickStore((store: OneClickStore) => store.setIsOneClickModeActive);
    const isPreviewModeActive = isExpressFeatureActive && isExpressModeActive;

    const handleExpressClick = () => {
        if (isExpressModeActive) {
            return;
        }

        GTMDataLayerPush({
            event: "spellcheck_select_option",
            option: "mentor_express"
        });

        setExpressModeActive(true);
    };

    const handleClassicClick = () => {
        if (!isExpressModeActive) {
            return;
        }

        GTMDataLayerPush({
            event: "spellcheck_select_option",
            option: "mentor_standard"
        });

        setExpressModeActive(false);
    };

    return (
        <>
            <SC.Desk>
                <SC.Editor isPreview={isPreviewModeActive}>
                    <StatusMessages />
                    {isPreviewModeActive ?
                        <ExpressPreview />
                        : <>
                            <SC.DeskContent ref={deskRef}>
                                <InlineAdviceWrapper deskRef={deskRef} />
                                <PadErrorWrapper>
                                    <Pad />
                                </PadErrorWrapper>
                            </SC.DeskContent>
                            <footer>
                                <Rim />
                            </footer>
                        </>}
                </SC.Editor>

                <SC.Advice isPreview={isPreviewModeActive}>
                    <AdviceErrorWrapper>
                        {isExpressFeatureActive && <CorrectionModeButtons
                            handleClassicButton={handleClassicClick}
                            handleOneClickButton={handleExpressClick}
                        />}
                        {!isPreviewModeActive && <AdvicePanel />}
                        {isPreviewModeActive ? <OneClickHandshake isPreview /> : <Handshake />}
                    </AdviceErrorWrapper>
                    <Profiler />
                    <MetaMenu />
                </SC.Advice>
            </SC.Desk>
        </>
    );
};

const mapStateToProps = (store: EditorState): SpellcheckLiteStateProps => ({
    featureFlags: selectFeatureFlags(store),
});

export const SpellcheckLite = connect(mapStateToProps)(Spellcheck);
